import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { getCurrentUser } from 'aws-amplify/auth';
import ApiService from '../services/api';

// Types
interface AdminUser {
  username: string;
  email: string;
  name: string;
  status: string;
  created_at?: string;
}

interface CreateAdminRequest {
  username: string;
  email: string;
  name: string;
  temporary_password?: string;
  send_email: boolean;
}

// Validation schema
const adminSchema: yup.ObjectSchema<CreateAdminRequest> = yup.object({
  username: yup.string().required('Username is required').min(3, 'Username must be at least 3 characters'),
  email: yup.string().email('Invalid email').required('Email is required'),
  name: yup.string().required('Full name is required'),
  temporary_password: yup.string().optional(),
  send_email: yup.boolean().default(true),
}) as yup.ObjectSchema<CreateAdminRequest>;

const Admin: React.FC = () => {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<string | null>(null);
  const [confirmResetMFA, setConfirmResetMFA] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<CreateAdminRequest>({
    resolver: yupResolver(adminSchema),
    defaultValues: {
      username: '',
      email: '',
      name: '',
      temporary_password: '',
      send_email: true,
    }
  });

  // Load admin users on component mount
  useEffect(() => {
    loadAdminUsers();
    getCurrentUserEmail();
  }, []);

  const getCurrentUserEmail = async () => {
    try {
      const user = await getCurrentUser();
      setUserEmail(user.signInDetails?.loginId || '');
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  };

  const loadAdminUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // TODO: Implement API call to get admin users
      // This will need a new backend endpoint to list users in the admin group
      const response = await ApiService.getAdminUsers();
      setAdminUsers(response);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load admin users';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: CreateAdminRequest) => {
    try {
      setError(null);
      setSuccess(null);
      
      // TODO: Implement API call to create admin user
      // This will need a new backend endpoint to create user and add to admin group
      await ApiService.createAdminUser(data);
      
      setSuccess(`Admin user ${data.username} created successfully`);
      setDialogOpen(false);
      reset();
      loadAdminUsers();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create admin user';
      setError(errorMessage);
    }
  };

  const handleDeleteAdmin = async (username: string) => {
    try {
      setError(null);
      setSuccess(null);
      
      // TODO: Implement API call to remove user from admin group (or delete entirely)
      await ApiService.removeAdminUser(username);
      
      setSuccess(`Admin user ${username} removed successfully`);
      setConfirmDeleteUser(null);
      loadAdminUsers();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to remove admin user';
      setError(errorMessage);
    }
  };

  const handleResetMFA = async (username: string) => {
    try {
      setError(null);
      setSuccess(null);
      
      const result = await ApiService.resetUserMFA(username);
      
      setSuccess(`MFA reset for ${username} successful. New temporary password: ${result.new_password}`);
      setConfirmResetMFA(null);
      loadAdminUsers();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to reset user MFA';
      setError(errorMessage);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Admin Panel
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAdminUsers}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Add Admin User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Admin Users List */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <AdminIcon color="primary" />
            <Typography variant="h6">
              Admin Users ({adminUsers.length})
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" mb={2}>
            Users with administrative privileges who can manage accounts and users across AWS environments.
            Admin users must be members of the 'user_account_admins' Cognito group.
          </Typography>

          {loading ? (
            <Typography>Loading admin users...</Typography>
          ) : adminUsers.length === 0 ? (
            <Typography color="text.secondary">
              No admin users found. Click "Add Admin User" to create the first administrator.
            </Typography>
          ) : (
            <List>
              {adminUsers.map((admin) => (
                <ListItem key={admin.username} divider>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="subtitle1">
                          {admin.name}
                        </Typography>
                        <Chip
                          label="Admin"
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Email: {admin.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Username: {admin.username}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Status: {admin.status}
                        </Typography>
                        {admin.created_at && (
                          <Typography variant="body2" color="text.secondary">
                            Created: {new Date(admin.created_at).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Reset MFA and password">
                        <IconButton
                          edge="end"
                          color="warning"
                          onClick={() => setConfirmResetMFA(admin.username)}
                        >
                          <SecurityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove admin privileges">
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => setConfirmDeleteUser(admin.username)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Create Admin User Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Admin User</DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Username"
                    fullWidth
                    error={!!errors.username}
                    helperText={errors.username?.message}
                  />
                )}
              />
              
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
              
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Full Name"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
              
              <Controller
                name="temporary_password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Temporary Password (optional)"
                    type="password"
                    fullWidth
                    helperText="Leave blank to auto-generate a secure password"
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Admin User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!confirmDeleteUser} onClose={() => setConfirmDeleteUser(null)}>
        <DialogTitle>Remove Admin User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove admin privileges from user "{confirmDeleteUser}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will remove the user from the admin group but will not delete their account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteUser(null)}>Cancel</Button>
          <Button 
            onClick={() => confirmDeleteUser && handleDeleteAdmin(confirmDeleteUser)}
            color="error"
            variant="contained"
          >
            Remove Admin Access
          </Button>
        </DialogActions>
      </Dialog>

      {/* MFA Reset Confirmation Dialog */}
      <Dialog open={!!confirmResetMFA} onClose={() => setConfirmResetMFA(null)}>
        <DialogTitle>Reset User MFA</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reset MFA and password for user "{confirmResetMFA}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Generate a new temporary password
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Force the user to set up MFA again
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Send an email with the new credentials
            </Typography>
          </Box>
          <Typography variant="body2" color="warning.main" sx={{ mt: 2, fontWeight: 'bold' }}>
            ⚠️ The user will lose access to their current MFA setup and must re-enroll.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmResetMFA(null)}>Cancel</Button>
          <Button 
            onClick={() => confirmResetMFA && handleResetMFA(confirmResetMFA)}
            color="warning"
            variant="contained"
          >
            Reset MFA & Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admin;